/**
 * AURA Useful Links Scanner
 * Discovers common accessibility/navigation links on the host page
 * and presents them in a dropdown for quick access.
 */

import { Logger } from '../../core/logger';
import { getIcon } from '../../ui/styles/icons';

const logger = new Logger('UsefulLinks');

interface DiscoveredLink {
  label: string;
  href: string;
  element?: HTMLElement;
}

const LINK_PATTERNS: { label: string; selectors: string[]; hrefPatterns: RegExp[] }[] = [
  {
    label: 'Skip to Content',
    selectors: ['[href="#main"]', '[href="#content"]', '.skip-link', '.skip-nav', '[class*="skip"]'],
    hrefPatterns: [/skip/i, /#main/i, /#content/i],
  },
  {
    label: 'Home Page',
    selectors: ['a[href="/"]', 'a[href="./"]', '[aria-label*="home" i]'],
    hrefPatterns: [/^\/?$/],
  },
  {
    label: 'Sitemap',
    selectors: ['a[href*="sitemap"]'],
    hrefPatterns: [/sitemap/i],
  },
  {
    label: 'Help / FAQ',
    selectors: ['a[href*="help"]', 'a[href*="faq"]', 'a[href*="support"]'],
    hrefPatterns: [/help|faq|support/i],
  },
  {
    label: 'Contact',
    selectors: ['a[href*="contact"]'],
    hrefPatterns: [/contact/i],
  },
  {
    label: 'Accessibility Statement',
    selectors: ['a[href*="accessibility"]'],
    hrefPatterns: [/accessibility/i],
  },
];

export const USEFUL_LINKS_STYLES = `
  .aura-useful-links {
    grid-column: 1 / -1 !important;
    background: var(--aura-bg-secondary, #f8f9fa) !important;
    border: 1px solid var(--aura-border, #dee2e6) !important;
    border-radius: var(--aura-radius-lg, 12px) !important;
    padding: 14px !important;
    display: flex !important;
    flex-direction: column !important;
    gap: 8px !important;
  }

  .aura-useful-links__header {
    display: flex !important;
    align-items: center !important;
    gap: 8px !important;
  }

  .aura-useful-links__icon {
    width: 28px !important;
    height: 28px !important;
    color: var(--aura-text-muted, #6c757d) !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
  }

  .aura-useful-links__icon svg {
    width: 28px !important;
    height: 28px !important;
  }

  .aura-useful-links__title {
    font-size: 14px !important;
    font-weight: 500 !important;
    color: var(--aura-text, #212529) !important;
  }

  .aura-useful-links__select {
    width: 100% !important;
    padding: 8px 16px !important;
    border: 1px solid var(--aura-border, #dee2e6) !important;
    border-radius: 8px !important;
    background: var(--aura-bg, #ffffff) !important;
    color: var(--aura-text, #212529) !important;
    font-size: 14px !important;
    font-family: inherit !important;
    cursor: pointer !important;
    appearance: auto !important;
  }

  .aura-useful-links__select:focus-visible {
    outline: 2px solid var(--aura-border-focus, #4a90d9) !important;
    outline-offset: 2px !important;
  }
`;

/**
 * Scan the host page and build the useful links dropdown.
 * Returns null if no useful links are found.
 */
export function createUsefulLinks(): HTMLElement | null {
  const links = scanPageForLinks();

  if (links.length === 0) {
    logger.info('No useful links found on page');
    return null;
  }

  logger.info('Discovered useful links', { count: links.length });

  const wrapper = document.createElement('div');
  wrapper.className = 'aura-useful-links';

  const header = document.createElement('div');
  header.className = 'aura-useful-links__header';

  const icon = document.createElement('span');
  icon.className = 'aura-useful-links__icon';
  icon.setAttribute('aria-hidden', 'true');
  icon.innerHTML = getIcon('usefulLinks');

  const title = document.createElement('span');
  title.className = 'aura-useful-links__title';
  title.textContent = 'Useful Links';

  header.appendChild(icon);
  header.appendChild(title);

  const select = document.createElement('select');
  select.className = 'aura-useful-links__select';
  select.setAttribute('aria-label', 'Useful Links');

  const placeholder = document.createElement('option');
  placeholder.value = '';
  placeholder.textContent = 'Select an option';
  placeholder.disabled = true;
  placeholder.selected = true;
  select.appendChild(placeholder);

  links.forEach((link, index) => {
    const option = document.createElement('option');
    option.value = String(index);
    option.textContent = link.label;
    select.appendChild(option);
  });

  select.addEventListener('change', () => {
    const idx = parseInt(select.value, 10);
    const link = links[idx];
    if (!link) return;

    if (link.element) {
      link.element.focus();
      link.element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      logger.info('Navigated to useful link via element', { label: link.label });
    } else if (link.href) {
      window.location.href = link.href;
      logger.info('Navigated to useful link via href', { label: link.label, href: link.href });
    }

    // Reset dropdown after navigation
    select.selectedIndex = 0;
  });

  wrapper.appendChild(header);
  wrapper.appendChild(select);
  return wrapper;
}

function scanPageForLinks(): DiscoveredLink[] {
  const found = new Map<string, DiscoveredLink>();

  for (const pattern of LINK_PATTERNS) {
    // Try CSS selectors first
    for (const selector of pattern.selectors) {
      try {
        const el = document.querySelector<HTMLAnchorElement>(selector);
        if (el && !found.has(pattern.label)) {
          found.set(pattern.label, {
            label: pattern.label,
            href: el.href || el.getAttribute('href') || '',
            element: el,
          });
        }
      } catch (_) {
        // Selector may be invalid on some pages
      }
    }

    // Fallback: scan all links by href pattern
    if (!found.has(pattern.label)) {
      const allLinks = document.querySelectorAll<HTMLAnchorElement>('a[href]');
      for (const link of allLinks) {
        const href = link.getAttribute('href') || '';
        if (pattern.hrefPatterns.some((re) => re.test(href))) {
          found.set(pattern.label, {
            label: pattern.label,
            href: link.href,
            element: link,
          });
          break;
        }
      }
    }
  }

  return Array.from(found.values());
}
